/**
 * src/services/billingService.ts
 * =============================================================================
 * Cliente del flujo de facturación contra el Worker (api.ploter.me).
 *
 * Endpoints:
 *   POST /billing/checkout-url  → genera URL de Polar Checkout para un plan
 *   POST /billing/portal-url    → URL del customer portal Polar
 *   POST /me/quota              → estado de almacenamiento del workspace activo
 *
 * Para upgradearse: el cliente llama a startCheckout(plan), recibe la URL,
 * y hace window.location = url. Tras pagar, Polar redirige a /?upgraded=1
 * que el frontend interpreta para mostrar un toast de éxito + invalidar
 * la query de quota para refrescar el cap.
 * =============================================================================
 */

import { api, ApiError } from '@/lib/api'

/**
 * Planes contratables.
 *
 * `space_combo` se eliminó el 2026-08-17 al pasar a miembros ilimitados: su
 * única función era ampliar aforo y espacio a la vez, y para el espacio ya
 * está `storage_addon`. Un concepto menos que explicar al cliente.
 */
export type BillingPlan = 'pro' | 'storage_addon' | 'space_base'

export interface QuotaInfo {
  plan: 'free' | 'pro'
  bytesUsed: number
  bytesLimit: number
  bytesAvailable: number
  uploadTtlSeconds: number
  /** true si la org es un espacio compartido con sub `space_base` activa. */
  isPaidSpace: boolean
  /** true si es un espacio compartido SIN activar: se puede leer, pero no
   *  subir nada hasta contratar los €5/mes. */
  needsActivation: boolean
  /**
   * true si el espacio SÍ está pagado pero ya no le queda ningún admin con
   * Pro activo. Los espacios son un añadido de Pro, no un producto suelto.
   *
   * Queda en solo lectura: los documentos siguen ahí y se pueden ver y
   * descargar, solo se bloquea subir cosas nuevas. Y el cron cancela su
   * suscripción en Polar, porque cobrar por un espacio inutilizable sería
   * cobrar por nada.
   */
  ownerProMissing: boolean
}

export interface SubscriptionRow {
  polarSubscriptionId: string
  plan: BillingPlan
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | string
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
}

export class BillingError extends Error {
  code: string
  constructor(code: string, message?: string) {
    super(message ?? code)
    this.code = code
  }
}

function rebrand(e: unknown): BillingError {
  if (e instanceof ApiError) return new BillingError(e.code, e.message)
  if (e instanceof Error) return new BillingError('request_failed', e.message)
  return new BillingError('request_failed')
}

/**
 * Inicia el flujo de upgrade. El backend puede:
 *  - `action='checkout'` con `url` → redirige a Polar Checkout (caso normal).
 *  - `action='reactivated'` con `url=null` → la sub estaba pendiente de
 *    cancelar y el backend acaba de hacer PATCH para mantenerla. El caller
 *    NO redirige, simplemente refresca el estado y muestra toast.
 */
export interface CheckoutResponse {
  action: 'checkout' | 'reactivated' | 'already_active'
  url: string | null
}

export async function getCheckout(
  plan: BillingPlan,
  opts: { spaceId?: string } = {},
): Promise<CheckoutResponse> {
  try {
    const r = await api<CheckoutResponse>('/billing/checkout-url', {
      method: 'POST',
      body: opts.spaceId ? { plan, spaceId: opts.spaceId } : { plan },
    })
    if (!r?.action) throw new BillingError('invalid_response')
    return r
  } catch (e) {
    if (e instanceof BillingError) throw e
    throw rebrand(e)
  }
}

/**
 * Estado actual de la cuota del workspace activo. Se llama al cargar
 * MisDocumentosPage para pintar la barra "47 GB / 50 GB" y al volver de
 * checkout para refrescar.
 */
export async function getQuota(spaceId?: string): Promise<QuotaInfo> {
  try {
    return await api<QuotaInfo>('/me/quota', {
      method: 'POST',
      body: spaceId ? { spaceId } : {},
    })
  } catch (e) {
    throw rebrand(e)
  }
}

/**
 * Lista las suscripciones de la org activa del usuario. Cada add-on de
 * storage cuenta como una sub independiente (modelo "Opción 1" en Polar).
 */
export async function getSubscriptions(): Promise<SubscriptionRow[]> {
  try {
    const r = await api<{ subscriptions: SubscriptionRow[] }>('/me/subscriptions', {
      method: 'POST',
      body: {},
    })
    return r.subscriptions ?? []
  } catch (e) {
    throw rebrand(e)
  }
}

/**
 * Cancela una suscripción. Por defecto cancela "al final del periodo" — el
 * usuario sigue siendo Pro hasta que se acabe el mes ya pagado. Si llamas
 * con `immediate: true` el beneficio se pierde al instante (caso edge:
 * refund inmediato).
 */
export async function cancelSubscription(
  subscriptionId: string,
  options: { immediate?: boolean } = {},
): Promise<void> {
  try {
    await api('/billing/cancel-subscription', {
      method: 'POST',
      body: { subscriptionId, immediate: options.immediate === true },
    })
  } catch (e) {
    throw rebrand(e)
  }
}

/**
 * URL del Customer Portal de Polar para una suscripción del usuario: ahí se
 * cambia la tarjeta y se descargan las facturas. Reintroducido el 2026-09-04
 * porque desde el producto no existía NINGUNA vía de actualizar el método de
 * pago (el flujo "cancelar y recontratar" nunca llega a un checkout).
 */
export async function getPortalUrl(subscriptionId: string): Promise<string> {
  try {
    const r = await api<{ url: string }>('/billing/portal-url', {
      method: 'POST',
      body: { subscriptionId },
    })
    if (!r?.url) throw new BillingError('invalid_response')
    return r.url
  } catch (e) {
    if (e instanceof BillingError) throw e
    throw rebrand(e)
  }
}

/**
 * Formatea bytes a unidades legibles: "300 MB", "50 GB", "1,5 GB".
 *
 * Los GB llevan como mucho un decimal y solo si aporta algo: antes salía
 * "50.0 GB", con un cero que no dice nada y además con punto decimal aunque
 * la pantalla estuviera en español. El separador lo pone `Intl` según el
 * idioma: `locale` es opcional y, sin él, se usa el del navegador.
 */
export function formatBytes(bytes: number, locale?: string): string {
  const numero = (valor: number, decimales: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: decimales }).format(valor)
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${numero(bytes / 1024, 0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${numero(bytes / (1024 * 1024), 0)} MB`
  return `${numero(bytes / (1024 * 1024 * 1024), 1)} GB`
}
